<script setup>
    import { defineExpose, defineEmits, ref, inject, computed, getCurrentInstance } from 'vue';
    import { PositionInfoRequest } from '@/models/management/managementModel';
    import { useManagementStore } from '@/stores/management/managementStore';
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 利用者の管理ストア
    const managementStore = useManagementStore();
    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // 詳細リクエスト項目
    const positionInfoRequest = ref({});

    // 入力欄活性コントロール
    // 基本情報
    const disabled = computed(() => {
        return positionInfoRequest.value.TouDataFlag;
    });

    // ID入力欄の活性/非活性の状態を制御する。初期値は活性で入力可能。
    const idInputDisabled = ref(false);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * 初期処理 親画面から利用可能関数
     */
    async function setInfo(PositionPk) {
        // 初期化する。
        idInputDisabled.value = false;

        // 詳細リクエスト項目の初期化
        positionInfoRequest.value = new PositionInfoRequest();
        if (PositionPk) {
            positionInfoRequest.value.PositionPk = PositionPk;

            // 詳細取得
            await getDetail(positionInfoRequest.value.PositionPk);

            // APIから取得した契約割り当てが未設定の場合は、nullもしくは空配列が返却されるので、モデルクラス上は空配列で設定する。
            if (Utils.isNullEmptyArray(positionInfoRequest.value.CmpContPks)) {
                positionInfoRequest.value.CmpContPks = [];
            }

            // 編集モードの場合は、IDの入力欄は非活性にする。
            idInputDisabled.value = true;
        }
        // 企業契約情報取得
        await getCintractList();

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalMasterDetail .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }
    defineExpose({
        setInfo,
    });

    /**
     * 詳細情報処理
     */
    async function getDetail(PositionPk) {
        const queryParam = {
            PositionPk: PositionPk,
        };
        const response = await managementStore.getPosDtlAction(API_URL, queryParam);
        if (response.success) {
            positionInfoRequest.value = Object.assign(positionInfoRequest.value, managementStore.positionInfo);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 登録or更新処理
     */
    async function createDetail() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        //const requestBody = positionInfoRequest.value;
        let requestBody = positionInfoRequest.value;

        // 表示順が設定されていない場合はnullを設定する。
        if (Utils.isNullEmpty(requestBody.DisplayOrder)) {
            requestBody.DisplayOrder = null;
        }

        // 契約割り当てが選択されていない場合はnullを設定する。
        if (Utils.isNullEmptyArray(requestBody.CmpContPks)) {
            requestBody.CmpContPks = null;
        }

        if (positionInfoRequest.value.PositionPk === '') {
            const response = await managementStore.postPosDtlAction(API_URL, requestBody);
            if (response.success) {
                closeAndUpdate();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        } else {
            const response = await managementStore.putPosDtlAction(API_URL, requestBody);
            if (response.success) {
                closeAndUpdate();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';
        // ID
        if (Utils.isNullEmpty(positionInfoRequest.value.PositionId)) {
            message += i18n.global.t('ManagementPositionDetail.message.message1') + '\n';
        } else {
            // 文字数
            if (Utils.checkLengthOfUserId(positionInfoRequest.value.PositionId)) {
                message += i18n.global.t('ManagementPositionDetail.message.message2') + '\n';
            }
            // 入力可能文字のチェック
            if (!Utils.isMatchPatternAlphabetNumberSymbolForManagementId(positionInfoRequest.value.PositionId)) {
                message += i18n.global.t('ManagementPositionDetail.message.message7') + '\n';
            }
        }
        // 日本語名
        if (Utils.isNullEmpty(positionInfoRequest.value.PositionNmJ)) {
            message += i18n.global.t('ManagementPositionDetail.message.message3') + '\n';
        } else {
            // 文字数
            if (Utils.checkLengthOfManagementShortNameString(positionInfoRequest.value.PositionNmJ)) {
                message += i18n.global.t('ManagementPositionDetail.message.message4') + '\n';
            }
        }
        // 英語名
        if (!Utils.isNullEmpty(positionInfoRequest.value.PositionNmE)) {
            // 文字数
            if (Utils.checkLengthOfManagementShortNameString(positionInfoRequest.value.PositionNmE)) {
                message += i18n.global.t('ManagementPositionDetail.message.message5') + '\n';
            }
            // 入力可能文字のチェック
            if (!Utils.isMatchPatternAlphabetNumberSymbolForManagementNameEn(positionInfoRequest.value.PositionNmE)) {
                message += i18n.global.t('ManagementPositionDetail.message.message8') + '\n';
            }
        }
        // 表示順
        if (!Utils.isNullEmpty(positionInfoRequest.value.DisplayOrder)) {
            // 文字数
            if (Utils.checkLengthOfDisplayOrderNumber(positionInfoRequest.value.DisplayOrder)) {
                message += i18n.global.t('ManagementPositionDetail.message.message6') + '\n';
            }
        }
        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    /**
     * 企業契約情報取得処理
     */
    async function getCintractList() {
        const queryParam = {};
        const response = await managementStore.getCmpConAction(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 親画面で実装必須関数
     */
    const emit = defineEmits(['detailModal:cancel', 'detailModal:closeAndUpdate']);
    function cancel() {
        // 親画面のキャンセル処理を呼び出す
        emit('detailModal:cancel');
    }
    function closeAndUpdate() {
        // 親画面の、モーダルを閉じて、一覧画面の再描画を行う処理を呼び出す。
        emit('detailModal:closeAndUpdate');
    }
</script>

<template>
    <section id="overlay" :style="{ display: 'block' }">
        <article id="modalMasterDetail" class="modal" style="display: block" :style="{ display: 'block' }">
            <h3 class="textCenter">{{ $t('ManagementPositionDetail.parts.pageTitle1') }}</h3>
            <section class="modalContents" style="height: calc(100% - 145px)">
                <section class="form dtWidth170">
                    <h4>{{ $t('ManagementPositionDetail.parts.pageTitle2') }}</h4>
                    <dl>
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementPositionDetail.parts.dl2') }} </label>
                                </li>
                                <li class="must">
                                    <span>{{ $t('ManagementPositionDetail.parts.dl1') }} </span>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li>
                                    <input type="text" v-model="positionInfoRequest.PositionId" :disabled="idInputDisabled" />
                                </li>
                            </ul>
                        </dd>
                    </dl>
                    <dl>
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementPositionDetail.parts.dl3') }} </label>
                                </li>
                                <li class="must">
                                    <span>{{ $t('ManagementPositionDetail.parts.dl1') }} </span>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li>
                                    <input type="text" v-model="positionInfoRequest.PositionNmJ" :disabled="disabled" />
                                </li>
                            </ul>
                        </dd>
                    </dl>
                    <dl>
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementPositionDetail.parts.dl4') }} </label>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li>
                                    <input type="text" v-model="positionInfoRequest.PositionNmE" :disabled="disabled" />
                                </li>
                            </ul>
                        </dd>
                    </dl>
                    <dl>
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementPositionDetail.parts.dl5') }} </label>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li>
                                    <input type="number" v-model="positionInfoRequest.DisplayOrder" :disabled="disabled" />
                                </li>
                            </ul>
                        </dd>
                    </dl>
                    <h4 style="margin-top: 40px">{{ $t('ManagementPositionDetail.parts.pageTitle3') }}</h4>
                    <dl>
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ $t('ManagementPositionDetail.parts.dl6') }} </label>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <section class="innerTable" style="height: 329px">
                                <table class="table">
                                    <tbody>
                                        <tr v-for="(data, index) in managementStore.contractList" :key="index">
                                            <td style="width: 20px">
                                                <input type="checkbox" :id="'data5-' + (index + 1)" class="checkbox" v-model="positionInfoRequest.CmpContPks" :value="data.CmpContPk" :disabled="data.TouDataFlag" />
                                                <label class="checkbox" :for="'data5-' + (index + 1)" />
                                            </td>
                                            <td style="width: auto">
                                                <span>{{ index + 1 }}. {{ data.CmpContNm }} ({{ data.CmpContPeriodS }} - {{ data.CmpContPeriodE }})</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>
                        </dd>
                    </dl>
                </section>
            </section>
            <section class="modalFooter">
                <ul class="btnArea center">
                    <li>
                        <button type="button" class="btnNormal closeModal" @click="cancel()">{{ $t('ManagementPositionDetail.parts.button1') }}</button>
                    </li>
                    <li>
                        <button type="button" class="btnPrimary closeModal" @click="createDetail()" :disabled="disabledStore.isDisabled">{{ $t('ManagementPositionDetail.parts.button2') }}</button>
                    </li>
                </ul>
            </section>
        </article>
    </section>
</template>

<style></style>
